/**
 * pages/MyAccount.jsx
 * -------------------
 * Buyer dashboard — clean mobile-first experience using the unified
 * dashboard framework (v2.71.27) + property module (v2.71.26) +
 * recently-viewed store (v2.71.24).
 *
 * UX principles (per v2.71.28 spec):
 *   • one-thumb usability — top stats + quick actions reachable without
 *     stretching; rails stack vertically on mobile
 *   • minimal clutter — compact stat row, 4 quick chips, 4 rails (5 items
 *     each), no charts
 *   • fast loading — Promise.all over 3 small endpoints; existing
 *     skeleton fallback for slow networks
 *   • simple navigation — every rail has a single "View all →" link;
 *     no nested menus
 *
 * Features wired:
 *   ✅ Saved properties           – /api/properties/me/saved
 *   ✅ Recently viewed             – @store/recentlyViewed (localStorage)
 *   ✅ Inquiry history             – /api/enquiries/me (added v2.71.28)
 *   ✅ Property alerts             – /api/saved-searches
 *   ✅ Profile management          – sidebar nav + Edit profile chip
 *   ⏳ Scheduled visits            – DELIBERATELY OMITTED. No backend
 *                                    schema exists; the spec said "DO NOT
 *                                    overload" / "minimal clutter". Will
 *                                    ship as a clean separate feature
 *                                    when the backend `site_visits`
 *                                    collection is designed (see ROADMAP).
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Heart, ClipboardList, MessageCircle, Crown, Eye, MapPin, Bell,
  ArrowRight, Building2, Calendar, User as UserIcon,
} from "lucide-react";

import { useAuth } from "../lib/AuthContext";
import { formatINR } from "../shared/services/api";
import api from "../lib/api";

import {
  DashboardLayout, StatCard, QuickActions, RecentItemsList, Guard,
} from "../features/dashboard";
import { useRecentlyViewedItems } from "../store";
// v2.80 — Refresh saved / enquiries / alerts when underlying domains change.
import { useDataSync } from "../lib/dataSync";

/** Single date helper kept inline — no need for a util module. */
function ago(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (isNaN(t)) return "";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export default function MyAccount() {
  const { user, loading: authLoading } = useAuth();
  const recent = useRecentlyViewedItems();
  const [saved, setSaved] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback((cancel) => {
    // 4 small endpoints in parallel — total round-trip ≈ slowest one.
    Promise.allSettled([
      api.get("/properties/me/saved"),
      api.get("/enquiries/me"),
      api.get("/saved-searches"),
      api.get("/leads/me"),
    ]).then((res) => {
      if (cancel) return;
      const [savedR, enqR, srchR, statR] = res;
      if (savedR.status === "fulfilled") {
        const d = savedR.value.data;
        setSaved(Array.isArray(d) ? d : d?.results || []);
      }
      if (enqR.status === "fulfilled") setEnquiries(enqR.value.data?.results || []);
      if (srchR.status === "fulfilled") setAlerts(srchR.value.data?.results || []);
      if (statR.status === "fulfilled") setStats(statR.value.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancel = false;
    loadAll(cancel);
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // v2.80 — When a property is mutated (e.g. admin deletes a saved
  // listing) OR a new enquiry/lead lands, re-pull the rails. Cheap:
  // 4 parallel small endpoints.
  useDataSync(["properties", "enquiries", "leads"], () => {
    if (user) loadAll(false);
  });

  if (authLoading) return <DashboardLayout title="My Account" sidebar />;
  if (!user) return <Navigate to="/login?next=/account" replace />;

  const planLabel = user?.subscription?.active ? user.subscription.tier : "Free";

  return (
    <DashboardLayout
      title="My Account"
      subtitle={user.name ? `Welcome back, ${user.name.split(" ")[0]}.` : "Welcome back."}
      actions={
        <Guard perm="plan:purchase">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-3 py-2 bg-[#0055FF] text-white text-[13px] font-semibold rounded-sm hover:bg-[#0044CC] transition-colors"
            data-testid="my-account-upgrade-cta"
          >
            <Crown size={14} /> Upgrade
          </Link>
        </Guard>
      }
    >
      {/* ── Stats row — 4 compact cards. On mobile (390 px) renders as 2x2.
            Each card is one-thumb tappable height (~76 px). */}
      <section className="mb-5" data-testid="account-stats-row">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            label="Saved"
            icon={Heart}
            value={loading ? "…" : saved.length}
            hint="Bookmarked"
            testid="my-account-stat-saved"
          />
          <StatCard
            label="Recently"
            icon={Eye}
            value={recent.length}
            hint="Last 20"
            testid="my-account-stat-recent"
          />
          <StatCard
            label="Callbacks"
            icon={MessageCircle}
            value={loading ? "…" : enquiries.length}
            hint="Sent"
            testid="my-account-stat-inquiries"
          />
          <StatCard
            label="Plan"
            icon={Crown}
            value={planLabel}
            hint={user?.subscription?.active ? "Active" : "Upgrade"}
            testid="my-account-stat-plan"
          />
        </div>
      </section>

      {/* ── Quick actions — 4 chips, all touch-friendly. */}
      <section className="mb-5">
        <QuickActions
          actions={[
            { label: "Browse", to: "/", icon: ClipboardList },
            { label: "Map", to: "/map", icon: MapPin },
            { label: "Alerts", to: "/account/alerts", icon: Bell },
            { label: "Edit Profile", to: "/profile", icon: UserIcon },
          ]}
        />
      </section>

      {/* ── Rails: stacked on mobile, 2-col on lg. Compact cards (5 rows
            each). No charts. */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        {/* Recently Viewed */}
        <RecentItemsList
          title="Recently Viewed"
          items={recent.slice(0, 5)}
          emptyHint="Open a property and it'll appear here."
          viewAllTo={recent.length > 5 ? "/" : undefined}
          testid="my-account-recent"
          renderItem={(p) => (
            <Link to={`/property/${p.id}`} className="flex items-center gap-3 group" data-testid={`recent-link-${p.id}`}>
              {p.image ? (
                <img src={p.image} alt="" loading="lazy" decoding="async" width="56" height="42"
                  className="w-14 h-[42px] object-cover rounded-sm bg-[#F4F4F5] flex-shrink-0" />
              ) : (
                <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#A1A1AA]">
                  <Building2 size={16} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">{p.title}</div>
                <div className="text-[11px] text-[#71717A] mt-0.5 truncate">
                  {p.area || "—"}
                  {p.price ? <span className="font-semibold text-[#27272A]"> · {formatINR(p.price)}</span> : null}
                </div>
              </div>
              <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0" />
            </Link>
          )}
        />

        {/* Saved Properties */}
        <RecentItemsList
          title="Saved Properties"
          items={loading ? [] : saved.slice(0, 5)}
          emptyHint={loading ? "Loading…" : "Tap the heart on any property to save it."}
          viewAllTo="/saved"
          testid="my-account-saved"
          renderItem={(p) => (
            <Link to={`/property/${p.id}`} className="flex items-center gap-3 group" data-testid={`saved-link-${p.id}`}>
              {p.images?.[0] ? (
                <img src={p.images[0]} alt="" loading="lazy" decoding="async" width="56" height="42"
                  className="w-14 h-[42px] object-cover rounded-sm bg-[#F4F4F5] flex-shrink-0" />
              ) : (
                <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#A1A1AA]">
                  <Building2 size={16} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">{p.title}</div>
                <div className="text-[11px] text-[#71717A] mt-0.5 truncate">
                  {p.main_area || p.area || "—"}
                  {p.price ? <span className="font-semibold text-[#27272A]"> · {formatINR(p.price)}</span> : null}
                </div>
              </div>
              <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0" />
            </Link>
          )}
        />

        {/* Callback History — replaces legacy Inquiry History (v2.71.67) */}
        <RecentItemsList
          title="My Callbacks"
          items={loading ? [] : enquiries.slice(0, 3)}
          emptyHint={loading ? "Loading…" : "Request a callback on any property — your history appears here."}
          viewAllTo={enquiries.length > 3 ? "/account/enquiries" : undefined}
          testid="my-account-inquiries"
          renderItem={(e) => (
            <Link to={`/property/${e.property_id}`} className="flex items-center gap-3 group" data-testid={`enquiry-link-${e.id}`}>
              <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#71717A]">
                {e.kind === "callback_request" ? <MessageCircle size={16} /> : <MessageCircle size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                  {e.property_title || "Property"}
                </div>
                <div className="text-[11px] text-[#71717A] mt-0.5 truncate flex items-center gap-1.5">
                  <span className={
                    e.status === "closed" ? "text-[#16A34A]"
                    : e.status === "contacted" ? "text-[#0055FF]"
                    : "text-[#A1A1AA]"
                  }>
                    {e.status === "new" ? "Pending" : (e.status || "new").replace("_", " ")}
                  </span>
                  <span>·</span>
                  <span>{ago(e.created_at)}</span>
                </div>
              </div>
              <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0" />
            </Link>
          )}
        />

        {/* Property Alerts (saved searches) */}
        <RecentItemsList
          title="Property Alerts"
          items={loading ? [] : alerts.slice(0, 3)}
          emptyHint={loading ? "Loading…" : "Save a search to get alerts when new matches appear."}
          viewAllTo={alerts.length > 3 ? "/account/alerts" : undefined}
          testid="my-account-alerts"
          renderItem={(a) => {
            // Build a 1-line summary of the saved filters
            const f = a.filters || {};
            const bits = [
              f.main_area, f.bhk, f.property_type, f.listing_intent,
            ].filter(Boolean);
            return (
              <Link
                to={`/?${new URLSearchParams(
                  Object.fromEntries(Object.entries(f).filter(([k, v]) => v && k !== "priceRange" && k !== "maxMetro"))
                ).toString()}`}
                className="flex items-center gap-3 group"
                data-testid={`alert-link-${a.id}`}
              >
                <div className="w-14 h-[42px] bg-[#F4F4F5] rounded-sm flex-shrink-0 flex items-center justify-center text-[#71717A]">
                  <Bell size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-[#09090B] truncate group-hover:text-[#0055FF]">
                    {a.name || "Untitled alert"}
                  </div>
                  <div className="text-[11px] text-[#71717A] mt-0.5 truncate">
                    {bits.length ? bits.join(" · ") : "Custom filter"}
                  </div>
                </div>
                <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#0055FF] flex-shrink-0" />
              </Link>
            );
          }}
        />
      </section>

      {/* Footer note — explains the deliberately-omitted Visits feature
          so testers don't think it's a bug. Tiny, low-emphasis. */}
      <div className="mt-8 flex items-center gap-2 text-[11px] text-[#A1A1AA]" data-testid="my-account-footer-note">
        <Calendar size={12} />
        <span>Scheduled property visits coming soon.</span>
      </div>
    </DashboardLayout>
  );
}
